import type { PrecompiledWeek, PrecompiledSet } from "../types/Entities";
import type {
  ExerciseProgression,
  ExerciseProgressionSource,
} from "../types/Programmazione";

// Feature: collega/scollega + "aggiornamento disponibile".
//
// An embedded exercise progression carries a snapshot of the library weeks plus
// `source = { kind:"library", precompiledProgressionId, libraryVersion, detached? }`.
// When linked (not detached), if the library's current updatedAt differs from
// the stored libraryVersion, an update is available.

/** True when the progression is a live link to a library template (kind
 *  "library" and not detached). */
export function isLinked(progression?: ExerciseProgression | null): boolean {
  if (!progression) return false;
  return progression.source.kind === "library" && !progression.source.detached;
}

/** True when linked AND the library has a newer version than the snapshot. */
export function hasLibraryUpdate(
  progression: ExerciseProgression | null | undefined,
  libraryUpdatedAt: string | null | undefined
): boolean {
  if (!progression || !isLinked(progression)) return false;
  if (progression.source.kind !== "library") return false;
  const stored = progression.source.libraryVersion;
  if (!stored || !libraryUpdatedAt) return false;
  return stored !== libraryUpdatedAt;
}

// Per-set fields that hold athlete-entered execution data / media we want to
// preserve when re-syncing the structure from the library.
const PRESERVED_SET_KEYS: (keyof PrecompiledSet)[] = [
  "workReps",
  "workLoad",
  "image",
  "video",
  "imageUrl",
  "videoUrl",
  "trainerMedia",
  "athleteMedia",
];

/**
 * Re-sync the embedded weeks to the library's latest, preserving athlete data.
 * The library provides the up-to-date structure; for each [weekIndex][setIndex]
 * still present, we carry over the preserved per-set fields from the old
 * snapshot so executed loads/reps/media aren't lost.
 */
export function mergeLibraryUpdate(
  currentWeeks: PrecompiledWeek[] | undefined,
  libraryWeeks: PrecompiledWeek[]
): PrecompiledWeek[] {
  const nextWeeks: PrecompiledWeek[] = JSON.parse(JSON.stringify(libraryWeeks));
  (nextWeeks || []).forEach((week, wi) => {
    const oldWeek = currentWeeks?.[wi];
    if (!oldWeek?.sets || !week.sets) return;
    week.sets.forEach((set: PrecompiledSet, si: number) => {
      const oldSet = oldWeek.sets?.[si];
      if (!oldSet) return;
      PRESERVED_SET_KEYS.forEach((key) => {
        if (oldSet[key] !== undefined && oldSet[key] !== null) {
          (set as any)[key] = oldSet[key];
        }
      });
    });
  });
  return nextWeeks;
}

/** True when attaching a picked progression should prompt the user to choose
 *  between replacing and appending — i.e. the exercise already has at least one
 *  configured week to preserve. */
export function shouldPromptAttachMode(
  existing: ExerciseProgression | null | undefined,
): boolean {
  return (existing?.weeks?.length ?? 0) > 0;
}

/**
 * PROG-ATTACH-MERGE — combine a newly picked library progression with the
 * exercise's existing one.
 *  - "replace": swap to the picked library snapshot, link preserved (the legacy
 *     attach behavior).
 *  - "append": keep the existing weeks and add the picked ones after them (e.g.
 *     an existing 2-week block + a template that continues from week 3),
 *     renumbering `weekNumber` to array position (the convention used
 *     everywhere else). The result mixes two templates, so its source becomes
 *     inline — it can no longer track a single library entry for
 *     "aggiornamento disponibile".
 */
export function mergeAttachedProgression(
  existing: ExerciseProgression | null | undefined,
  picked: {
    weeks: PrecompiledWeek[];
    source: Extract<ExerciseProgressionSource, { kind: "library" }>;
  },
  mode: "replace" | "append",
): ExerciseProgression {
  const existingWeeks = existing?.weeks ?? [];
  if (mode === "replace" || existingWeeks.length === 0) {
    return { weeks: picked.weeks, source: picked.source };
  }
  const combined: PrecompiledWeek[] = JSON.parse(
    JSON.stringify([...existingWeeks, ...picked.weeks]),
  );
  const weeks = combined.map((w, i) => ({ ...w, weekNumber: i + 1 }));
  return { weeks, source: { kind: "inline" } };
}

/** Produce the "linked to latest library" progression after an "Aggiorna". */
export function applyLibraryUpdate(
  progression: ExerciseProgression,
  libraryWeeks: PrecompiledWeek[],
  libraryUpdatedAt: string | null | undefined
): ExerciseProgression {
  if (progression.source.kind !== "library") return progression;
  return {
    ...progression,
    weeks: mergeLibraryUpdate(progression.weeks, libraryWeeks),
    source: {
      ...progression.source,
      detached: false,
      libraryVersion: libraryUpdatedAt ?? progression.source.libraryVersion,
    },
  };
}
